import { Info } from 'lucide-react'
import { Button } from '@/common/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/common/ui/popover'

const PRECEDENCE_LEVELS = [
	{
		level: 1,
		label: 'ตั้งค่าเฉพาะผู้เล่น',
		description: 'แอดมินตั้งค่า Preset ให้ผู้เล่นโดยตรง',
	},
	{
		level: 2,
		label: 'Forced Preset (Flex Message / แคมเปญ)',
		description: 'Preset ที่กำหนดในข้อความ Flex หรือแคมเปญ',
	},
	{
		level: 3,
		label: 'เงื่อนไขโปรโมชั่น/กระเป๋า',
		description: 'Preset จาก session โปรโมชั่นที่กำลังใช้งาน',
	},
	{
		level: 4,
		label: 'Tag ผู้เล่น',
		description: 'Preset ที่กำหนดใน Tag (ใช้เมื่อไม่มี Preset จากโปรโมชั่น)',
	},
	{
		level: 5,
		label: 'Preset ตั้งต้นของเกม',
		description: 'ค่า Default Preset ที่ตั้งไว้สำหรับเกมนั้น',
	},
	{
		level: 6,
		label: 'Preset ตั้งต้นของค่ายเกม',
		description: 'ค่า Default Preset ของค่ายเกม (ลำดับต่ำสุด)',
	},
] as const

interface RtpPresetPrecedenceTooltipProps {
	highlightLevel?: 1 | 2 | 3 | 4 | 5 | 6
}

export function RtpPresetPrecedenceTooltip({
	highlightLevel,
}: RtpPresetPrecedenceTooltipProps) {
	return (
		<Popover>
			<PopoverTrigger asChild>
				<Button
					type="button"
					variant="outline"
					size="sm"
					className="h-7 gap-1.5 px-2 text-xs text-muted-foreground"
					aria-label="ดูลำดับความสำคัญของ RTP Preset"
				>
					<Info className="h-3.5 w-3.5" />
					ลำดับความสำคัญ
				</Button>
			</PopoverTrigger>
			<PopoverContent className="w-80 p-4" side="right" align="start">
				<div className="space-y-3 text-sm">
					<p className="font-semibold">ลำดับความสำคัญของ RTP Preset</p>
					<p className="text-xs text-muted-foreground">
						ระบบจะเลือก Preset ตามลำดับนี้ — ลำดับสูงกว่าจะมีผลก่อนเสมอ
					</p>
					<ol className="space-y-2">
						{PRECEDENCE_LEVELS.map(({ level, label, description }) => {
							const isHighlighted = level === highlightLevel
							return (
								<li
									key={level}
									className={`flex gap-2 rounded-md px-2 py-1.5 ${
										isHighlighted ? 'bg-primary/10 ring-1 ring-primary/20' : ''
									}`}
								>
									<span
										className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
											isHighlighted
												? 'bg-primary text-primary-foreground'
												: 'bg-muted text-muted-foreground'
										}`}
									>
										{level}
									</span>
									<div className="min-w-0">
										<p
											className={`font-medium leading-tight ${isHighlighted ? 'text-foreground' : 'text-foreground/80'}`}
										>
											{label}
										</p>
										<p className="text-xs text-muted-foreground">
											{description}
										</p>
									</div>
								</li>
							)
						})}
					</ol>
					{highlightLevel && (
						<p className="border-t pt-2 text-xs text-muted-foreground">
							★ ช่องนี้ตั้งค่าระดับที่{' '}
							<span className="font-semibold text-foreground">
								{highlightLevel}
							</span>
						</p>
					)}
				</div>
			</PopoverContent>
		</Popover>
	)
}
